import { call, put, takeLatest, all, fork } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { kycService } from "@/services/kycService";
import {
  // Fetch KYC Status
  fetchKYCStatusRequest,
  fetchKYCStatusSuccess,
  fetchKYCStatusFailure,
  fetchKYCStatusNotFound,

  // Business Details
  submitBusinessDetailsRequest,
  submitBusinessDetailsSuccess,
  submitBusinessDetailsFailure,

  // Personal Details
  submitPersonalDetailsRequest,
  submitPersonalDetailsSuccess,
  submitPersonalDetailsFailure,

  // Document Upload
  uploadDocumentsRequest,
  uploadDocumentsSuccess,
  uploadDocumentsFailure,

  // Address Verification
  uploadAddressProofRequest,
  uploadAddressProofSuccess,
  uploadAddressProofFailure,

  // Video Verification
  uploadVideoKYCRequest,
  uploadVideoKYCSuccess,
  uploadVideoKYCFailure,
  updateKycFieldsRequest,
  updateKycFieldsSuccess,
  updateKycFieldsFailure,
} from "../slices/kyc.slice";
import { KYCStatusResponse, KYCSubmissionResponse } from "@/types/models/kyc";

// Helper function to determine if a KYC API response indicates success
function isKycResponseValid(response: any): boolean {
  const wrapper: any = response as any;

  // Check for wrapper format first ({ valid: true, message: "...", data: {...} })
  if (
    wrapper.valid === true ||
    wrapper.message?.toLowerCase().includes("success")
  ) {
    return true;
  }

  // If no wrapper format, check if response looks like a successful KYC object
  if (
    wrapper.id &&
    (wrapper.business_name ||
      wrapper.gst_number ||
      wrapper.aadhaar_document_front ||
      wrapper.address_proof_document ||
      wrapper.video_file)
  ) {
    // Response is a KYC object directly - this indicates success
    return true;
  }

  return false;
}

// Fetch KYC Status Saga
function* fetchKYCStatusSaga(action: PayloadAction<{ storeId: string }>) {
  try {
    console.debug("[kyc.saga] fetchKYCStatusSaga called with action:", action);
    console.debug("[kyc.saga] calling kycService.getKycStatus()");

    const response: KYCStatusResponse = yield call(kycService.getKycStatus);

    console.debug("[kyc.saga] received response from getKycStatus:", response);

    // Normalize candidate payload: prefer response.data (wrapper) otherwise response itself
    const candidate =
      response && (response as any).data ? (response as any).data : response;

    // If the API returned a wrapper with valid=true, or the candidate looks like a KYC object (has id)
    const hasValidFlag = !!(response && (response as any).valid === true);
    const looksLikeKycObject = !!(candidate && (candidate as any).id);

    console.debug(
      "[kyc.saga] candidate payload:",
      candidate,
      "hasValidFlag:",
      hasValidFlag,
      "looksLikeKycObject:",
      looksLikeKycObject,
    );

    if (hasValidFlag || looksLikeKycObject) {
      yield put(fetchKYCStatusSuccess(candidate));
    } else {
      // If KYC record doesn't exist (404) or message indicates not found, treat as not-found for new users
      if (
        (response &&
          (response as any).message &&
          (response as any).message.includes("not found")) ||
        (response &&
          (response as any).message &&
          (response as any).message.includes("Not Found"))
      ) {
        yield put(fetchKYCStatusNotFound());
      } else {
        yield put(
          fetchKYCStatusFailure(
            (response && (response as any).message) ||
              "Failed to fetch KYC status",
          ),
        );
      }
    }
  } catch (error: unknown) {
    const axiosError = error as {
      response?: { status?: number; data?: { message?: string } };
      message?: string;
    };

    const errorMessage =
      axiosError.response?.data?.message ||
      axiosError.message ||
      "Failed to fetch KYC status";

    console.debug("[kyc.saga] fetchKYCStatusSaga caught error:", error);

    // If it's a 404 error, treat it as "not_started" stage for new users
    if (
      axiosError.response?.status === 404 ||
      errorMessage.includes("not found") ||
      errorMessage.includes("Not Found")
    ) {
      console.debug(
        "[kyc.saga] 404 detected - dispatching fetchKYCStatusNotFound",
      );
      yield put(fetchKYCStatusNotFound());
    } else {
      yield put(fetchKYCStatusFailure(errorMessage));
    }
  }
}

// Submit Business Details Saga
function* submitBusinessDetailsSaga(
  action: PayloadAction<{ storeId: string; formData: FormData }>,
) {
  try {
    const { formData } = action.payload;

    const response: KYCSubmissionResponse = yield call(
      kycService.submitBusinessDetails,
      formData,
    );

    console.debug("[kyc.saga] submitBusinessDetails response:", response);

    // Use the helper function for consistent validation
    const isValid = isKycResponseValid(response);

    console.debug("[kyc.saga] submitBusinessDetails validation check:", {
      wrapperValid: (response as any).valid,
      wrapperValidType: typeof (response as any).valid,
      wrapperValidEquals: (response as any).valid === true,
      messageCheck: (response as any).message
        ?.toLowerCase()
        .includes("success"),
      hasKycId: !!(response as any).data?.id || !!(response as any).id,
      hasBusinessName:
        !!(response as any).data?.business_name ||
        !!(response as any).business_name,
      finalIsValid: isValid,
      fullResponse: response,
    });

    if (isValid) {
      // Extract KYC data from response - prefer data object if it exists
      const responseData = (response as any).data || response;
      let submittedData: any = {};

      if (responseData && responseData.id) {
        // The API returned the full KYC object — use it to populate Redux state.
        submittedData = responseData;
      } else {
        // Convert FormData back to object for preservation
        formData.forEach((value, key) => {
          if (key === "business_logo") {
            // File objects can't be serialized, skip them
            return;
          }
          if (key === "state" || key === "city") {
            if (!submittedData.address) submittedData.address = {};
            submittedData.address[key] = value;
          } else {
            submittedData[key] = value;
          }
        });
      }

      // Debug

      console.debug(
        "[kyc.saga] submitBusinessDetails success, submittedData:",
        submittedData,
        "raw response:",
        response,
      );

      yield put(submitBusinessDetailsSuccess({ submittedData }));
    } else {
      yield put(
        submitBusinessDetailsFailure(
          response.message || "Failed to submit business details",
        ),
      );
    }
  } catch (error: unknown) {
    const errorMessage =
      (
        error as {
          response?: { data?: { message?: string } };
          message?: string;
        }
      ).response?.data?.message ||
      (error as { message?: string }).message ||
      "Failed to submit business details";
    yield put(submitBusinessDetailsFailure(errorMessage));
  }
}

// Submit Personal Details Saga
function* submitPersonalDetailsSaga(
  action: PayloadAction<{ storeId: string; formData: FormData }>,
) {
  try {
    const { formData } = action.payload;
    const response: KYCSubmissionResponse = yield call(
      kycService.submitPersonalDetails,
      formData,
    );

    console.debug("[kyc.saga] submitPersonalDetails response:", response);

    const isValid = isKycResponseValid(response);

    console.debug("[kyc.saga] submitPersonalDetails isValid check:", {
      wrapperValid: (response as any).valid,
      wrapperValidType: typeof (response as any).valid,
      wrapperValidEquals: (response as any).valid === true,
      messageCheck: (response as any).message
        ?.toLowerCase()
        .includes("success"),
      hasKycId: !!(response as any).id,
      hasGstNumber: !!(response as any).gst_number,
      finalIsValid: isValid,
    });

    if (isValid) {
      yield put(submitPersonalDetailsSuccess());
    } else {
      yield put(
        submitPersonalDetailsFailure(
          response.message || "Failed to submit personal details",
        ),
      );
    }
  } catch (error: unknown) {
    const errorMessage =
      (
        error as {
          response?: { data?: { message?: string } };
          message?: string;
        }
      ).response?.data?.message ||
      (error as { message?: string }).message ||
      "Failed to submit personal details";
    yield put(submitPersonalDetailsFailure(errorMessage));
  }
}

// Upload Documents Saga
function* uploadDocumentsSaga(
  action: PayloadAction<{
    storeId: string;
    formData: FormData;
    isUpdate?: boolean;
  }>,
) {
  try {
    const { formData, isUpdate = false } = action.payload;
    const response: KYCSubmissionResponse = yield call(
      kycService.uploadDocuments,
      formData,
      isUpdate,
    );

    console.debug("[kyc.saga] uploadDocuments response:", response);

    const isValid = isKycResponseValid(response);

    if (isValid) {
      yield put(uploadDocumentsSuccess());
    } else {
      yield put(
        uploadDocumentsFailure(
          response.message || "Failed to upload documents",
        ),
      );
    }
  } catch (error: unknown) {
    const errorMessage =
      (
        error as {
          response?: { data?: { message?: string } };
          message?: string;
        }
      ).response?.data?.message ||
      (error as { message?: string }).message ||
      "Failed to upload documents";
    yield put(uploadDocumentsFailure(errorMessage));
  }
}

// Upload Address Proof Saga
function* uploadAddressProofSaga(
  action: PayloadAction<{
    storeId: string;
    formData: FormData;
    isUpdate?: boolean;
  }>,
) {
  try {
    const { formData, isUpdate = false } = action.payload;
    const response: KYCSubmissionResponse = yield call(
      kycService.uploadAddressProof,
      formData,
      isUpdate,
    );

    console.debug("[kyc.saga] uploadAddressProof response:", response);

    const isValid = isKycResponseValid(response);

    if (isValid) {
      yield put(uploadAddressProofSuccess());
    } else {
      yield put(
        uploadAddressProofFailure(
          response.message || "Failed to upload address proof",
        ),
      );
    }
  } catch (error: unknown) {
    const errorMessage =
      (
        error as {
          response?: { data?: { message?: string } };
          message?: string;
        }
      ).response?.data?.message ||
      (error as { message?: string }).message ||
      "Failed to upload address proof";
    yield put(uploadAddressProofFailure(errorMessage));
  }
}

// Upload Video KYC Saga
function* uploadVideoKYCSaga(
  action: PayloadAction<{
    storeId: string;
    formData: FormData;
    isUpdate?: boolean;
  }>,
) {
  try {
    const { formData, isUpdate = false } = action.payload;
    const response: KYCSubmissionResponse = yield call(
      kycService.uploadVideoKyc,
      formData,
      isUpdate,
    );

    console.debug("[kyc.saga] uploadVideoKYC response:", response);

    const isValid = isKycResponseValid(response);

    if (isValid) {
      yield put(uploadVideoKYCSuccess());
    } else {
      yield put(
        uploadVideoKYCFailure(response.message || "Failed to upload video KYC"),
      );
    }
  } catch (error: unknown) {
    const errorMessage =
      (
        error as {
          response?: { data?: { message?: string } };
          message?: string;
        }
      ).response?.data?.message ||
      (error as { message?: string }).message ||
      "Failed to upload video KYC";
    yield put(uploadVideoKYCFailure(errorMessage));
  }
}

// Update KYC Fields Saga (PATCH for resubmissions)
function* updateKycFieldsSaga(
  action: PayloadAction<{
    storeId: string;
    fields: Record<string, unknown>;
  }>,
) {
  try {
    const { storeId, fields } = action.payload;
    const response: KYCSubmissionResponse = yield call(
      kycService.updateKycFields,
      fields,
    );

    console.debug("[kyc.saga] updateKycFields response:", response);

    const isValid = isKycResponseValid(response);

    if (isValid) {
      yield put(updateKycFieldsSuccess());
      // Refresh KYC status after successful update
      yield put(fetchKYCStatusRequest({ storeId }));
    } else {
      yield put(
        updateKycFieldsFailure(
          response.message || "Failed to update KYC fields",
        ),
      );
    }
  } catch (error: unknown) {
    const errorMessage =
      (
        error as {
          response?: { data?: { message?: string } };
          message?: string;
        }
      ).response?.data?.message ||
      (error as { message?: string }).message ||
      "Failed to update KYC fields";
    yield put(updateKycFieldsFailure(errorMessage));
  }
}

// Watcher Sagas
function* watchFetchKYCStatus() {
  yield takeLatest(fetchKYCStatusRequest.type, fetchKYCStatusSaga);
}

function* watchSubmitBusinessDetails() {
  yield takeLatest(
    submitBusinessDetailsRequest.type,
    submitBusinessDetailsSaga,
  );
}

function* watchSubmitPersonalDetails() {
  yield takeLatest(
    submitPersonalDetailsRequest.type,
    submitPersonalDetailsSaga,
  );
}

function* watchUploadDocuments() {
  yield takeLatest(uploadDocumentsRequest.type, uploadDocumentsSaga);
}

function* watchUploadAddressProof() {
  yield takeLatest(uploadAddressProofRequest.type, uploadAddressProofSaga);
}

function* watchUploadVideoKYC() {
  yield takeLatest(uploadVideoKYCRequest.type, uploadVideoKYCSaga);
}

function* watchUpdateKycFields() {
  yield takeLatest(updateKycFieldsRequest.type, updateKycFieldsSaga);
}

// Root KYC Saga
export function* kycSaga() {
  yield all([
    fork(watchFetchKYCStatus),
    fork(watchSubmitBusinessDetails),
    fork(watchSubmitPersonalDetails),
    fork(watchUploadDocuments),
    fork(watchUploadAddressProof),
    fork(watchUploadVideoKYC),
    fork(watchUpdateKycFields),
  ]);
}

export default kycSaga;
